/**
 * plugins/orgchart.ts — Organograma hierárquico (div.org-chart)
 *
 * Modos de contexto:
 *   node  — clicou em div.org-node: editar role/name/info/avatar, variante,
 *            cores custom (bg/border/text), estrutura (add-child, add-sibling, mover, excluir)
 *   chart — clicou no fundo do .org-chart: editar legenda, adicionar nó raiz
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, rgbToHex, closestInside, moveSibling } from '../types';

type OcVariant =
    | 'org-n-root' | 'org-n-dir' | 'org-n-manager' | 'org-n-staff'
    | 'org-n-highlight' | 'org-n-success' | 'org-n-danger' | 'org-n-ghost';

const OC_VARIANTS: OcVariant[] = [
    'org-n-root', 'org-n-dir', 'org-n-manager', 'org-n-staff',
    'org-n-highlight', 'org-n-success', 'org-n-danger', 'org-n-ghost',
];

export class OrgChartPlugin implements VtbPlugin {
    readonly panelId = 'vtb-orgchart';
    readonly selectorHints = ['div.org-chart', '.org-chart', '.org-node', '[data-vtb-id="org-chart"]'];

    // ─── Detecção ─────────────────────────────────────────────────────────────

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        const chart = closestInside(target, visualEditor, e => 
            e.classList.contains('org-chart') || e.dataset.vtbId === 'org-chart'
        );
        if (!chart) return null;

        const node = closestInside(target, chart, e => 
            e.classList.contains('org-node') || e.dataset.vtbId === 'org-node'
        );
        if (node) {
            const li = node.parentElement as HTMLElement;
            return { type: this.panelId, label: 'Nó de Organograma', el: node, container: li, extra: { mode: 'node', chart } };
        }

        return { type: this.panelId, label: 'Organograma', el: chart, extra: { mode: 'chart', chart } };
    }

    // ─── Ativação do painel ───────────────────────────────────────────────────

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        const mode   = ctx.extra?.mode as string;
        const isNode = mode === 'node';

        // Mostrar/ocultar linhas
        this._rowDisplay(registry, 'vtb-oc-node-row',   isNode  ? 'flex' : 'none');
        this._rowDisplay(registry, 'vtb-oc-color-row',  isNode  ? 'flex' : 'none');
        this._rowDisplay(registry, 'vtb-oc-struct-row', isNode  ? 'flex' : 'none');
        this._rowDisplay(registry, 'vtb-oc-chart-row',  !isNode ? 'flex' : 'none');

        const chip = registry.q<HTMLElement>('vtb-oc-ctx');
        if (chip) chip.textContent = isNode ? 'Nó' : 'Organograma';

        if (isNode) {
            this._activateNode(ctx, registry);
        } else {
            this._activateChart(ctx, registry);
        }
    }

    private _activateNode(ctx: SmartCtx, registry: IVtbRegistry): void {
        const node = ctx.el;

        // Variante
        const varSel = registry.q<HTMLSelectElement>('vtb-oc-variant');
        if (varSel) {
            const current = OC_VARIANTS.find(v => node.classList.contains(v)) ?? 'org-n-staff';
            varSel.value = current;
        }

        // Texto
        this._setVal(registry, 'vtb-oc-role',   node.querySelector<HTMLElement>('.org-role')?.textContent ?? '');
        this._setVal(registry, 'vtb-oc-name',   node.querySelector<HTMLElement>('.org-name')?.textContent ?? '');
        this._setVal(registry, 'vtb-oc-info',   node.querySelector<HTMLElement>('.org-info')?.textContent ?? '');
        this._setVal(registry, 'vtb-oc-avatar', node.querySelector<HTMLElement>('.org-avatar')?.textContent?.trim() ?? '');

        // Cores (inline style → color picker)
        this._setColor(registry, 'vtb-oc-bg',     rgbToHex(node.style.backgroundColor) || '#ffffff');
        this._setColor(registry, 'vtb-oc-border',  rgbToHex(node.style.borderColor)     || '#e2e8f0');
        this._setColor(registry, 'vtb-oc-fg',      rgbToHex(node.style.color)           || '#1e293b');
    }

    private _activateChart(ctx: SmartCtx, registry: IVtbRegistry): void {
        const chart   = ctx.el;
        const caption = chart.querySelector<HTMLElement>('.org-caption');
        this._setVal(registry, 'vtb-oc-caption', caption?.textContent?.trim() ?? '');
    }

    // ─── Setup de eventos (chamado 1x no init) ────────────────────────────────

    setup(registry: IVtbRegistry): void {
        registry.q('vtb-oc-apply')?.addEventListener('click', () => this._applyNode(registry));

        // Live color preview
        registry.q<HTMLInputElement>('vtb-oc-bg')    ?.addEventListener('input', () => this._liveColors(registry));
        registry.q<HTMLInputElement>('vtb-oc-border') ?.addEventListener('input', () => this._liveColors(registry));
        registry.q<HTMLInputElement>('vtb-oc-fg')    ?.addEventListener('input', () => this._liveColors(registry));

        registry.q('vtb-oc-color-reset')?.addEventListener('click', () => this._resetColors(registry));

        // Estrutura
        registry.q('vtb-oc-add-child')  ?.addEventListener('click', () => this._addChild(registry));
        registry.q('vtb-oc-add-sibling') ?.addEventListener('click', () => this._addSibling(registry));
        registry.q('vtb-oc-move-prev')  ?.addEventListener('click', () => this._moveLi(registry, -1));
        registry.q('vtb-oc-move-next')  ?.addEventListener('click', () => this._moveLi(registry,  1));
        registry.q('vtb-oc-del')        ?.addEventListener('click', () => this._deleteNode(registry));

        // Chart-level
        registry.q('vtb-oc-caption-apply')?.addEventListener('click', () => this._applyCaption(registry));
        registry.q('vtb-oc-add-root')    ?.addEventListener('click', () => this._addRootNode(registry));
    }

    // ─── Operações de Nó ──────────────────────────────────────────────────────

    private _applyNode(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || ctx.extra?.mode !== 'node') return;
        const node = ctx.el;

        // Variant — remove inline colors ao mudar preset
        const variant = registry.q<HTMLSelectElement>('vtb-oc-variant')?.value as OcVariant;
        OC_VARIANTS.forEach(v => node.classList.remove(v));
        if (variant) node.classList.add(variant);
        node.style.removeProperty('background-color');
        node.style.removeProperty('border-color');
        node.style.removeProperty('color');

        // Avatar
        const avatarTxt = registry.q<HTMLInputElement>('vtb-oc-avatar')?.value?.trim() ?? '';
        let avatarEl = node.querySelector<HTMLElement>('.org-avatar');
        if (avatarTxt) {
            if (!avatarEl) {
                avatarEl = document.createElement('div');
                avatarEl.className = 'org-avatar';
                node.prepend(avatarEl);
            }
            avatarEl.textContent = avatarTxt;
        } else if (avatarEl) {
            avatarEl.remove();
        }

        // Role
        const roleVal = registry.q<HTMLInputElement>('vtb-oc-role')?.value?.trim() ?? '';
        let roleEl = node.querySelector<HTMLElement>('.org-role');
        if (roleVal) {
            if (!roleEl) {
                roleEl = document.createElement('span');
                roleEl.className = 'org-role';
                const nameEl = node.querySelector('.org-name');
                nameEl ? node.insertBefore(roleEl, nameEl) : node.appendChild(roleEl);
            }
            roleEl.textContent = roleVal;
        } else if (roleEl) {
            roleEl.remove();
        }

        // Name
        const nameVal = registry.q<HTMLInputElement>('vtb-oc-name')?.value?.trim() ?? '';
        let nameEl = node.querySelector<HTMLElement>('.org-name');
        if (nameVal) {
            if (!nameEl) {
                nameEl = document.createElement('span');
                nameEl.className = 'org-name';
                node.appendChild(nameEl);
            }
            nameEl.textContent = nameVal;
        } else if (nameEl) {
            nameEl.textContent = nameEl.textContent; // keep if empty input
        }

        // Info
        const infoVal = registry.q<HTMLInputElement>('vtb-oc-info')?.value?.trim() ?? '';
        let infoEl = node.querySelector<HTMLElement>('.org-info');
        if (infoVal) {
            if (!infoEl) {
                infoEl = document.createElement('span');
                infoEl.className = 'org-info';
                node.appendChild(infoEl);
            }
            infoEl.textContent = infoVal;
        } else if (infoEl) {
            infoEl.remove();
        }

        registry.sync();
    }

    private _liveColors(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || ctx.extra?.mode !== 'node') return;
        const node = ctx.el;
        const bg = registry.q<HTMLInputElement>('vtb-oc-bg')?.value;
        const bd = registry.q<HTMLInputElement>('vtb-oc-border')?.value;
        const fg = registry.q<HTMLInputElement>('vtb-oc-fg')?.value;
        if (bg) node.style.backgroundColor = bg;
        if (bd) node.style.borderColor      = bd;
        if (fg) node.style.color            = fg;
        registry.sync();
    }

    private _resetColors(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || ctx.extra?.mode !== 'node') return;
        const node = ctx.el;
        node.style.removeProperty('background-color');
        node.style.removeProperty('border-color');
        node.style.removeProperty('color');
        registry.sync();
    }

    private _addChild(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || ctx.extra?.mode !== 'node') return;
        const li = ctx.container as HTMLElement;

        let childUl = li.querySelector<HTMLElement>(':scope > ul');
        if (!childUl) {
            childUl = document.createElement('ul');
            li.appendChild(childUl);
        }

        const newLi   = document.createElement('li');
        const newNode = this._newNode();
        newLi.appendChild(newNode);
        childUl.appendChild(newLi);

        registry.sync();
        registry.selectEl(newNode);
    }

    private _addSibling(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || ctx.extra?.mode !== 'node') return;
        const li = ctx.container as HTMLElement;
        const ul = li.parentElement;
        if (!ul) return;

        const newLi   = document.createElement('li');
        const newNode = this._newNode();
        newLi.appendChild(newNode);
        li.insertAdjacentElement('afterend', newLi);

        registry.sync();
        registry.selectEl(newNode);
    }

    private _moveLi(registry: IVtbRegistry, dir: -1 | 1): void {
        const ctx = registry.activeCtx;
        if (!ctx || ctx.extra?.mode !== 'node') return;
        moveSibling(ctx.container as HTMLElement, dir);
        registry.sync();
    }

    private _deleteNode(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || ctx.extra?.mode !== 'node') return;
        const li = ctx.container as HTMLElement;
        const ul = li.parentElement;
        li.remove();
        // Remove ul vazio (evita conector orfão)
        if (ul && ul.children.length === 0) ul.remove();
        registry.clearSelection();
        registry.sync();
    }

    // ─── Operações de Chart ───────────────────────────────────────────────────

    private _applyCaption(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || ctx.extra?.mode !== 'chart') return;
        const chart   = ctx.el;
        const caption = registry.q<HTMLInputElement>('vtb-oc-caption')?.value?.trim() ?? '';
        let capEl = chart.querySelector<HTMLElement>('.org-caption');
        if (caption) {
            if (!capEl) {
                capEl = document.createElement('span');
                capEl.className = 'org-caption';
                chart.appendChild(capEl);
            }
            capEl.textContent = caption;
        } else if (capEl) {
            capEl.remove();
        }
        registry.sync();
    }

    private _addRootNode(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || ctx.extra?.mode !== 'chart') return;
        const chart = ctx.el;

        let rootUl = chart.querySelector<HTMLElement>(':scope > ul');
        if (!rootUl) {
            rootUl = document.createElement('ul');
            // Insere antes da legenda
            const caption = chart.querySelector('.org-caption');
            caption ? chart.insertBefore(rootUl, caption) : chart.appendChild(rootUl);
        }

        const newLi   = document.createElement('li');
        const newNode = this._newNode('org-n-root');
        newLi.appendChild(newNode);
        rootUl.appendChild(newLi);

        registry.sync();
        registry.selectEl(newNode);
    }

    // ─── Helpers ──────────────────────────────────────────────────────────────

    private _newNode(variant: OcVariant = 'org-n-staff'): HTMLElement {
        const node = document.createElement('div');
        node.className = 'org-node ' + variant;
        node.innerHTML = '<span class="org-role">Cargo</span><span class="org-name">Nome</span>';
        return node;
    }

    private _setVal(registry: IVtbRegistry, id: string, val: string): void {
        const el = registry.q<HTMLInputElement | HTMLTextAreaElement>(id);
        if (el) el.value = val;
    }

    private _setColor(registry: IVtbRegistry, id: string, val: string): void {
        const el = registry.q<HTMLInputElement>(id);
        if (el && val.startsWith('#')) el.value = val;
    }

    private _rowDisplay(registry: IVtbRegistry, id: string, display: string): void {
        const el = registry.q<HTMLElement>(id);
        if (el) el.style.display = display;
    }
}
